import { inject, injectable } from "inversify";
import SERVICE_IDENTIFIER from "../../constants/identifiers";
import events from "../../events/EventsObservable";
import SystemConfig, { CONFIG_PARAMS } from "../../system/SystemConfig";
import SystemPlatform, { PLATFORM } from "../../system/SystemPlatform";
import IScreenData from "./IScreenData";
import ScreenData from "./ScreenData";
import { BASE_ELEMENT_IDS } from "../BaseDOM";
import IApp from "../../engine/IApp";
import IUIMessages from "../messages/IUIMessages";
import { FULLSCREEN_EVENTS } from "./Fullscreen";
import addOnVisibilityChange from "./visibilityChange";
import IUIScreen from "./IUIScreen";

export const SCREEN_EVENTS = {
	SCREEN_RESIZE: "SCREEN_RESIZE",
	SCREEN_VISIBILITY_CHANGE: "SCREEN_VISIBILITY_CHANGE",
};

export const ORIENTATION = {
	LAND: "land",
	PORT: "port",
};

export const STAGE_MODES = {
	LAND: "land",
	PORT: "port",
};

@injectable()
export default abstract class AbstractUIScreen implements IUIScreen {
	protected config: SystemConfig;
	protected gameset: any;
	protected platform: SystemPlatform;
	protected app: IApp;
	protected uiMessages: IUIMessages;

	protected containerPaddingTop: number = 0;
	protected containerPaddingBottom: number = 0;
	protected containerPaddingLeft: number = 0;
	protected containerPaddingRight: number = 0;

	protected backgroundWidth = 1922;
	public uiType: string;

	protected constructor(
		@inject(SERVICE_IDENTIFIER.SystemConfig) config: SystemConfig,
		@inject(SERVICE_IDENTIFIER.EngineApp) app: IApp,
		@inject(SERVICE_IDENTIFIER.UIMessages) uiMessages: IUIMessages
	) {
		this.config = config;
		this.gameset = config.gameset;
		this.platform = config.platform;
		this.app = app;
		this.uiMessages = uiMessages;

		events.subscribe(FULLSCREEN_EVENTS.FULLSCREEN_CHANGE, (eventName, data) => {
			this.resize();
			setTimeout(() => this.resize(), 500);
		});
	}

	get BackgroundWidth() {
		return this.backgroundWidth;
	}

	set ContainerPaddingTop(newPadding: number) {
		this.containerPaddingTop = newPadding;
	}
	get ContainerPaddingTop() {
		return this.containerPaddingTop;
	}

	set ContainerPaddingLeft(newPadding: number) {
		this.containerPaddingLeft = newPadding;
	}
	get ContainerPaddingLeft() {
		return this.containerPaddingLeft;
	}

	set ContainerPaddingRight(newPadding: number) {
		this.containerPaddingRight = newPadding;
	}
	get ContainerPaddingRight() {
		return this.containerPaddingRight;
	}

	set ContainerPaddingBottom(newPadding: number) {
		this.containerPaddingBottom = newPadding;
	}
	get ContainerPaddingBottom() {
		return this.containerPaddingBottom;
	}

	init() {
		addOnVisibilityChange((focus) =>
			events.dispatch(SCREEN_EVENTS.SCREEN_VISIBILITY_CHANGE, { focus })
		);
		window.addEventListener("resize", () => this.resize());
	}

	public getStageMode(orientation: string): string {
		if (
			this.config.get(CONFIG_PARAMS.UI_DEVICE) == PLATFORM.DESKTOP
		) {
			return STAGE_MODES.LAND;
		}

		return orientation === ORIENTATION.PORT
			? STAGE_MODES.PORT
			: STAGE_MODES.LAND;
	}

	public getOrientation(): string {
		// let orientation = /*screen.msOrientation || screen.mozOrientation ||*/ (screen.orientation || {}).type;
		//
		// if(orientation != null) {
		//     return orientation.includes("port") ? ORIENTATION.PORT : ORIENTATION.LAND;
		// }

		if (
			document.documentElement.clientWidth <
			document.documentElement.clientHeight
		) {
			return ORIENTATION.PORT;
		}

		return ORIENTATION.LAND;
	}

	protected getContainerData(mode: string) {
		let widthOrig =
			mode === STAGE_MODES.PORT
				? this.gameset.stageHeight
				: this.gameset.stageWidth;
		let heightOrig =
			mode === STAGE_MODES.PORT
				? this.gameset.stageWidth
				: this.gameset.stageHeight;

		let widthPadding = this.containerPaddingLeft + this.containerPaddingRight;
		let heightPadding = this.containerPaddingTop + this.containerPaddingBottom;

		let width = widthOrig + widthPadding;
		let height = heightOrig + heightPadding;

		// if(this._platform.isVLT() && this._platform.getDisplaysCount() == 2) {
		//     origWidth = 1920;
		//     origHeight = 1080 * 2;
		// }

		let scale = getScale(window.innerWidth, window.innerHeight, width, height);

		let widthScaled = width * scale;
		let heightScaled = height * scale;
		let top = (window.innerHeight - heightScaled) / 2;
		let left = (window.innerWidth - widthScaled) / 2;

		top = top < 0 ? 0 : top;
		left = left < 0 ? 0 : left;

		return {
			scale,
			widthOrig,
			heightOrig,
			widthScaled,
			heightScaled,
			top,
			left,
		};
	}

	protected getCanvasData(containerData, mode: string) {
		let widthOrig = this.gameset.stageWidth;
		let heightOrig = this.gameset.stageHeight;

		let widthOffset = this.containerPaddingLeft + this.containerPaddingRight;
		let heightOffset = this.containerPaddingTop + this.containerPaddingBottom;

		let scale = getScale(
			containerData.widthScaled,
			containerData.heightScaled,
			widthOrig + widthOffset,
			heightOrig + heightOffset
		);

		let widthScaled = widthOrig * scale;
		let heightScaled = heightOrig * scale;

		return {
			scale,
			widthOrig,
			heightOrig,
			widthScaled,
			heightScaled,
		};
	}

	getScreenData(): IScreenData {
		let data = new ScreenData();
		let orientation = this.getOrientation();
		let mode = this.getStageMode(orientation);

		data.setOrientation(orientation);
		data.setMode(mode);

		let containerData = this.getContainerData(mode);
		data.setContainer(containerData);

		let canvasData = this.getCanvasData(containerData, mode);
		data.setCanvas(canvasData);

		return data;
	}

	public resize(screenData: IScreenData = this.getScreenData()) {
		this.resizeMode(screenData);
		this.resizePreloader(screenData);
		this.resizeContainer(screenData);
		this.resizeCanvas(screenData);
		this.resizeControls(screenData);
		this.resizeMessages(screenData);
	}

	protected resizeMode(data: IScreenData) {
		let wrapper = $(document.getElementById(BASE_ELEMENT_IDS.WRAPPER));

		if (data.mode === STAGE_MODES.PORT) {
			wrapper.removeClass("mode-landscape");
			wrapper.addClass("mode-portrait");
		} else {
			wrapper.removeClass("mode-portrait");
			wrapper.addClass("mode-landscape");
		}
	}

	protected resizePreloader(data: IScreenData) {
		$("#" + BASE_ELEMENT_IDS.LOADING).width(window.innerWidth);
		$("#" + BASE_ELEMENT_IDS.LOADING).height(window.innerHeight);
		$("#" + BASE_ELEMENT_IDS.LOADING_CONTAINER).css(
			"transform",
			"scale(" + data.canvasScale + ")"
		);
	}

	protected resizeContainer(data: IScreenData) {
		$("#" + BASE_ELEMENT_IDS.CONTAINER).css({
			top: data.containerTop,
			left: data.containerLeft,
			width: data.containerWidthScaled,
			height: data.containerHeightScaled,
		});
	}

	protected resizeCanvas(data: IScreenData): void {
		this.app.onResize(data);
		$(".mx-canvas-wrapper").css({
			top: this.containerPaddingTop * data.canvasScale,
			left: this.containerPaddingLeft * data.canvasScale,
		});
		$("#" + BASE_ELEMENT_IDS.AJAX_LOADING_CONTAINER).css(
			"transform",
			"scale(" + data.canvasScale + ")"
		);
	}

	protected resizeControls(data: IScreenData) {
		$(".mx-controls-wrapper").css({
			top: this.containerPaddingTop * data.canvasScale,
			left: this.containerPaddingLeft * data.canvasScale,
			width: data.canvasWidthOrig,
			height: data.canvasHeightOrig,
			transform: `scale(${data.canvasScale})`,
		});
	}

	protected resizeMessages(data: IScreenData) {
		this.uiMessages.onResize(data);
	}
}

export function getScale(
	outerWidth,
	outerHeight,
	innerWidth,
	innerHeight
): number {
	let scale = outerHeight / innerHeight;
	if (scale * innerWidth > outerWidth) {
		scale = outerWidth / innerWidth;
	}

	return scale;
}
